import { ElementLike } from "diagram-js/lib/model/Types";
import * as _ from "lodash";
import {
    ExecutionState,
    FailedStates,
} from "../../../model/process/ProcessInstanceDetails";

export class RenderStateResolver {
    public static GetRenderResultFromStatus(
        elementRegistry: { [key: string]: ElementLike },
        executedStepStatuses: Map<string, string | undefined> | undefined,
        typeResolver: (stepId: string) => ElementType | null,
    ): { [key: string]: RenderResult } {
        const result = {} as { [key: string]: RenderResult };
        if (executedStepStatuses !== undefined) {
            for (const stepId of Object.keys(executedStepStatuses)) {
                const shape = elementRegistry[stepId];
                if (shape) {
                    const type = typeResolver(stepId);
                    const state: ExecutionState = ExecutionState[
                        executedStepStatuses[
                            stepId
                        ] as unknown as ExecutionState
                    ] as unknown as ExecutionState;
                    if (_.includes(ConnectionTypes, type)) {
                        // Connections are drawn according to the shape they come from, just skip the logged ones.
                        continue;
                    } else {
                        const shapeResult: RenderResult = { id: stepId };
                        if (shape.type === "bpmn:SubProcess") {
                            if (_.includes(FailedStates, state)) {
                                shapeResult.nodeStatus =
                                    NodeStatusType.ScopeError;
                            } else if (state === ExecutionState.Finished) {
                                shapeResult.nodeStatus =
                                    NodeStatusType.ScopeSuccess;
                            } else {
                                shapeResult.nodeStatus =
                                    NodeStatusType.ScopeRunning;
                            }
                        } else if (
                            shape.type === "bpmn:IntermediateThrowEvent"
                        ) {
                            // We would no longer have to check Throw And Catch events as the issue that they reported the wrong type was fixed in ACC-6160.
                            // This is left so that old processes will be displayed correctly
                            shapeResult.nodeStatus = NodeStatusType.Error;
                        } else if (
                            shape.type === "bpmn:IntermediateCatchEvent"
                        ) {
                            // We would no longer have to check Throw And Catch events as the issue that they reported the wrong type was fixed in ACC-6160.
                            // This is left so that old processes will be displayed correctly
                            shapeResult.nodeStatus = NodeStatusType.Success;
                        } else {
                            if (state === ExecutionState.Finished) {
                                shapeResult.nodeStatus = NodeStatusType.Success;
                            } else if (_.includes(FailedStates, state)) {
                                shapeResult.nodeStatus = NodeStatusType.Error;
                            } else {
                                shapeResult.nodeStatus = NodeStatusType.Running;
                            }
                        }
                        result[shapeResult.id] = shapeResult;
                        if (shape.incoming) {
                            for (const incoming of shape.incoming) {
                                if (
                                    typeResolver(incoming.source.id) ===
                                    ElementType.SubProcessStartNode
                                ) {
                                    // The connection from the subprocess start node should be colored:
                                    result[incoming.id] = {
                                        id: incoming.id,
                                        nodeStatus: NodeStatusType.ScopeSuccess,
                                    } as RenderResult;
                                    // So should the incoming start node:
                                    result[incoming.source.id] = {
                                        id: incoming.source.id,
                                        nodeStatus: NodeStatusType.Success,
                                    } as RenderResult;
                                }
                            }
                        }

                        const executedStepIds =
                            Object.keys(executedStepStatuses);

                        if (shape.type === "bpmn:ExclusiveGateway") {
                            // In case the if or else branch is empty, the default rendering can go haywire. Therefore we need to manually check this.
                            const defaultConnectionId =
                                shape.businessObject.default?.id;
                            const elseConnection = shape.outgoing?.find(
                                (o) =>
                                    o.id === defaultConnectionId &&
                                    o.type === "bpmn:SequenceFlow",
                            );
                            const ifConnection = shape.outgoing?.find(
                                (o) =>
                                    o.id !== defaultConnectionId &&
                                    o.type === "bpmn:SequenceFlow",
                            );

                            if (
                                elseConnection?.target.incoming &&
                                elseConnection.target.incoming.filter(
                                    (i) => i.type === "bpmn:SequenceFlow",
                                )?.length > 1
                            ) {
                                // In case the else branch is empty (it points to a node with more than one incoming connection), we need to check if the IF target is executed.

                                if (
                                    ifConnection &&
                                    executedStepIds.indexOf(
                                        ifConnection.target.id,
                                    ) >= 0
                                ) {
                                    // If the if branch target is executed, the if branch should be green and the else gray
                                    result[elseConnection.id] = {
                                        id: elseConnection.id,
                                        nodeStatus: NodeStatusType.ScopeNotRun,
                                    } as RenderResult;
                                    result[ifConnection.id] = {
                                        id: ifConnection.id,
                                        nodeStatus: NodeStatusType.ScopeSuccess,
                                    } as RenderResult;
                                    continue;
                                } else if (
                                    executedStepIds.indexOf(
                                        elseConnection.target.id,
                                    ) >= 0
                                ) {
                                    // If the if branch target has not been executed, and the else target has, then the else branch should be green and the if gray
                                    result[elseConnection.id] = {
                                        id: elseConnection.id,
                                        nodeStatus: NodeStatusType.ScopeSuccess,
                                    } as RenderResult;
                                    result[ifConnection?.id as string] = {
                                        id: ifConnection?.id,
                                        nodeStatus: NodeStatusType.ScopeNotRun,
                                    } as RenderResult;
                                    continue;
                                }
                            } else if (
                                ifConnection?.target.incoming &&
                                ifConnection.target.incoming.filter(
                                    (i) => i.type === "bpmn:SequenceFlow",
                                ).length > 1
                            ) {
                                // And then the same check for if the if-branch is empty.
                                if (
                                    elseConnection &&
                                    executedStepIds.indexOf(
                                        elseConnection.target.id,
                                    ) >= 0
                                ) {
                                    result[ifConnection.id] = {
                                        id: ifConnection.id,
                                        nodeStatus: NodeStatusType.ScopeNotRun,
                                    } as RenderResult;
                                    result[elseConnection.id] = {
                                        id: elseConnection.id,
                                        nodeStatus: NodeStatusType.ScopeSuccess,
                                    } as RenderResult;
                                    continue;
                                } else if (
                                    executedStepIds.indexOf(
                                        ifConnection.target.id,
                                    ) >= 0
                                ) {
                                    result[ifConnection.id] = {
                                        id: ifConnection.id,
                                        nodeStatus: NodeStatusType.ScopeSuccess,
                                    } as RenderResult;
                                    result[elseConnection?.id as string] = {
                                        id: elseConnection?.id,
                                        nodeStatus: NodeStatusType.ScopeNotRun,
                                    } as RenderResult;
                                    continue;
                                }
                            }
                            // If neither of these cases are true, the default rendering will work.
                        }

                        if (shape.outgoing) {
                            for (const outgoing of shape.outgoing.filter(
                                (s) => s.type === "bpmn:SequenceFlow",
                            )) {
                                const targetExecuted =
                                    executedStepIds.indexOf(
                                        outgoing.target.id,
                                    ) >= 0;
                                if (
                                    targetExecuted &&
                                    type !== ElementType.Catch &&
                                    typeResolver(outgoing.target.id) ===
                                        ElementType.Catch &&
                                    _.includes(FailedStates, state)
                                ) {
                                    // If the execution failed, only the connection leading to a catch should be colored
                                    result[outgoing.id] = {
                                        id: outgoing.id,
                                        nodeStatus: NodeStatusType.ScopeSuccess,
                                    } as RenderResult;
                                } else if (
                                    targetExecuted &&
                                    (!_.includes(FailedStates, state) ||
                                        type === ElementType.Catch)
                                ) {
                                    // If the execution is successful, or if we're at a catch node, draw the outgoing connection
                                    result[outgoing.id] = {
                                        id: outgoing.id,
                                        nodeStatus: NodeStatusType.ScopeSuccess,
                                    } as RenderResult;
                                } else {
                                    result[outgoing.id] = {
                                        id: outgoing.id,
                                        nodeStatus: NodeStatusType.NotRun,
                                    };
                                }
                            }
                        }
                    }
                }
            }
        }

        for (const key in elementRegistry) {
            if (!result[key]) {
                // Shape has not been handled, so it has not been executed.
                const shapeWithNoStatus = elementRegistry[key];
                let type: ElementType | undefined | null = typeResolver(
                    shapeWithNoStatus.id,
                );
                if (type == null) {
                    // check if the type is something we want to track that does not exist in the element parameters:
                    type = this.getElementType(shapeWithNoStatus.type);
                }

                if (type == null) {
                    // We only want to color known types
                    continue;
                }
                if (shapeWithNoStatus.type === "bpmn:SequenceFlow") {
                    result[shapeWithNoStatus.id] = {
                        id: shapeWithNoStatus.id,
                        nodeStatus: NodeStatusType.ScopeNotRun,
                    } as RenderResult;
                } else if (shapeWithNoStatus.type === "bpmn:SubProcess") {
                    result[shapeWithNoStatus.id] = {
                        id: shapeWithNoStatus.id,
                        nodeStatus: NodeStatusType.ScopeNotRun,
                    } as RenderResult;
                } else {
                    result[shapeWithNoStatus.id] = {
                        id: shapeWithNoStatus.id,
                        nodeStatus: NodeStatusType.NotRun,
                    } as RenderResult;
                }
            }
        }

        return result;
    }

    private static getElementType(type: string): ElementType | undefined {
        switch (type) {
            case "bpmn:Task":
                return ElementType.Task;
            case "bpmn:SequenceFlow":
                return ElementType.SequenceFlow;
            case "bpmn:ExclusiveGateway":
                return ElementType.Decision;
            case "bpmn:StartEvent":
                return ElementType.Start;
            case "bpmn:EndEvent":
                return ElementType.Return;
            case "bpmn:IntermediateThrowEvent":
                return ElementType.Throw;
            case "bpmn:CallActivity":
                return ElementType.CallActivity;
            case "bpmn:SubProcess":
                return ElementType.SubProcess;
            case "bpmn:IntermediateCatchEvent":
                return ElementType.Catch;
            case "bpmn:ScriptTask":
                return ElementType.Expression;
            case "bpmn:InclusiveGateway":
                return ElementType.InclusiveGateway;
            case "bpmn:BusinessRuleTask":
                return ElementType.SharedState;
            case "bpmn:DataObjectReference":
                return ElementType.DataObjectReference;
            case "bpmn:DataStoreReference":
                return ElementType.DataStoreReference;
        }
    }
}

export interface RenderResult {
    id: string;
    nodeStatus?: NodeStatusType;
}

export enum NodeStatusType {
    Running = "Running",
    Success = "Success",
    Error = "Error",
    NotRun = "NotRun",
    ScopeRunning = "ScopeRunning",
    ScopeSuccess = "ScopeSuccess",
    ScopeError = "ScopeError",
    ScopeNotRun = "ScopeNotRun",
}

export enum ElementType {
    Start = "Start",
    Task = "Task",
    Decision = "Decision",
    SequenceFlow = "SequenceFlow",
    ConditionBranch = "ConditionBranch",
    Return = "Return",
    Throw = "Throw",
    CallActivity = "CallActivity",
    SubProcess = "SubProcess",
    ParallelForeach = "ParallelForeach",
    SequentialForeach = "SequentialForeach",
    While = "While",
    Expression = "Expression",
    SubProcessStartNode = "SubProcessStartNode",
    Catch = "Catch",
    InclusiveGateway = "InclusiveGateway",
    InclusiveDecisionBranch = "InclusiveDecisionBranch",
    IntermediateReturn = "IntermediateReturn",
    GlobalErrorHandler = "GlobalErrorHandler",
    TestTask = "TestTask",
    SharedState = "SharedState",
    Dmn = "Dmn",
    DataObjectReference = "DataObjectReference",
    DataStoreReference = "DataStoreReference",
}

export const ConnectionTypes = [
    ElementType.InclusiveDecisionBranch,
    ElementType.ConditionBranch,
    ElementType.SequenceFlow,
];
